/**
 * Shared KPI counter animation.
 *
 * @package HelloElementorChild
 */

var COUNTER_DURATION_MS = 2000;

export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function parseCounterValue(value) {
  var match = String(value || '').match(/([^0-9]*)([0-9.,]+)([^0-9]*)/);

  if (!match) {
    return null;
  }

  var numStr = match[2];
  var isDecimal = numStr.indexOf(',') !== -1;
  var parsed = parseFloat(numStr.replace(/\./g, '').replace(',', '.'));

  if (Number.isNaN(parsed)) {
    return null;
  }

  return {
    prefix: match[1],
    suffix: match[3],
    isDecimal: isDecimal,
    decimalPlaces: isDecimal ? (match[2].split(',')[1] || '').length : 0,
    target: parsed,
  };
}

export function formatCounterNumber(value, isDecimal, decimalPlaces) {
  if (isDecimal) {
    var places = typeof decimalPlaces === 'number' ? decimalPlaces : 2;

    try {
      return new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: places,
        maximumFractionDigits: places,
        // it-IT di default non raggruppa sotto 10.000; 'always' forza 1.000 / 7.418.
        useGrouping: 'always',
      }).format(value);
    } catch (err) {
      return formatItalianNumberFallback(value, places);
    }
  }

  try {
    return new Intl.NumberFormat('it-IT', {
      maximumFractionDigits: 0,
      useGrouping: 'always',
    }).format(Math.floor(value));
  } catch (err) {
    return formatItalianNumberFallback(Math.floor(value), 0);
  }
}

/**
 * Fallback manuale: migliaia con "." e decimali con ",".
 *
 * @param {number} value
 * @param {number} decimalPlaces
 * @return {string}
 */
function formatItalianNumberFallback(value, decimalPlaces) {
  var numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return String(value);
  }

  var sign = numeric < 0 ? '-' : '';
  var abs = Math.abs(numeric);

  if (decimalPlaces > 0) {
    var fixed = abs.toFixed(decimalPlaces);
    var parts = fixed.split('.');

    return sign + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
  }

  return sign + String(Math.floor(abs)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function easeOutQuad(t) {
  return 1 - (1 - t) * (1 - t);
}

export function setCounterFinal(counterEl, value) {
  if (!counterEl) {
    return;
  }

  if (counterEl._camstCounterFrame) {
    cancelAnimationFrame(counterEl._camstCounterFrame);
    counterEl._camstCounterFrame = null;
  }

  var parsed = parseCounterValue(value);

  if (!parsed) {
    counterEl.textContent = value;
    return;
  }

  counterEl.textContent =
    parsed.prefix + formatCounterNumber(parsed.target, parsed.isDecimal, parsed.decimalPlaces) + parsed.suffix;
}

export function animateCounter(counterEl, value, reducedMotion) {
  if (!counterEl) {
    return;
  }

  var parsed = parseCounterValue(value);

  if (!parsed) {
    counterEl.textContent = value;
    return;
  }

  if (counterEl._camstCounterFrame) {
    cancelAnimationFrame(counterEl._camstCounterFrame);
    counterEl._camstCounterFrame = null;
  }

  var formatted =
    parsed.prefix + formatCounterNumber(parsed.target, parsed.isDecimal, parsed.decimalPlaces) + parsed.suffix;

  if (reducedMotion) {
    counterEl.textContent = formatted;
    return;
  }

  var start = null;

  function frame(timestamp) {
    if (!start) {
      start = timestamp;
    }

    var t = Math.min(1, (timestamp - start) / COUNTER_DURATION_MS);
    var current = parsed.target * easeOutQuad(t);

    counterEl.textContent =
      parsed.prefix + formatCounterNumber(current, parsed.isDecimal, parsed.decimalPlaces) + parsed.suffix;

    if (t < 1) {
      counterEl._camstCounterFrame = requestAnimationFrame(frame);
    } else {
      counterEl._camstCounterFrame = null;
      counterEl.textContent = formatted;
    }
  }

  counterEl._camstCounterFrame = requestAnimationFrame(frame);
}

export function killCounterFrames(root, selector) {
  var query = selector || '[data-camst-counter]';

  root.querySelectorAll(query).forEach(function (counterEl) {
    if (counterEl._camstCounterFrame) {
      cancelAnimationFrame(counterEl._camstCounterFrame);
      counterEl._camstCounterFrame = null;
    }
  });
}
